<!-- PedidosCardPds.vue — Diseño responsivo: TV 3×3 / tablet 2 col / móvil 1 col -->
<template>
  <div :class="['pds-card', claseUrgencia]">
    <!-- Franja header con color de prioridad -->
    <div :class="['pds-card-header', clasePrioridadHeader]">
      <div class="pds-header-left">
        <span class="pds-numero">#{{ pedido.numero_pedido }}</span>
        <span class="pds-prioridad-tag">{{ pedido.prioridad }}</span>
      </div>
      <span :class="['pds-estatus', claseEstatus]">{{ pedido.estatus_almacen }}</span>
    </div>

    <!-- Cliente -->
    <div class="pds-cliente-bloque">
      <span class="pds-cliente-num">{{ pedido.cliente_numero }}</span>
      <span class="pds-cliente-nombre">{{ pedido.cliente }}</span>
    </div>

    <!-- Métricas: compromiso + días + artículos -->
    <div class="pds-metricas">
      <div class="pds-metrica">
        <span class="pds-m-label">Compromiso</span>
        <span class="pds-m-val">{{ formatFecha(pedido.fecha_compromiso) }}</span>
      </div>
      <div class="pds-metrica pds-metrica-center">
        <span class="pds-m-label">Días restantes</span>
        <span :class="['pds-dias', claseDias]">{{ labelDias }}</span>
      </div>
      <div class="pds-metrica pds-metrica-right">
        <span class="pds-m-label">Arts / Piezas</span>
        <span class="pds-m-val">
          {{ pedido.total_articulos ?? "—" }} /
          {{ pedido.total?.toLocaleString("es-MX") ?? "—" }}
        </span>
      </div>
    </div>

    <!-- Notas — visibles si existen -->
    <div v-if="pedido.notas" class="pds-notas">
      <i class="bi bi-chat-left-text-fill pds-notas-icon"></i>
      <span class="pds-notas-texto">{{ pedido.notas }}</span>
    </div>

    <!-- Quién dio de alta el pedido — discreto, al pie de la card -->
    <div v-if="pedido.creado_por" class="pds-capturista">
      <i class="bi bi-person-fill"></i>
      <span>Capturó: {{ pedido.creado_por }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  pedido: { type: Object, required: true },
});

function formatFecha(f) {
  if (!f) return "Sin asignar";
  return new Date(f + "T00:00:00").toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const labelDias = computed(() => {
  const d = props.pedido.dias_restantes;
  if (d == null) return "—";
  if (d < 0) return `${Math.abs(d)}d atraso`;
  if (d === 0) return "Vence hoy";
  return `${d} días`;
});

const claseDias = computed(() => {
  const d = props.pedido.dias_restantes;
  if (d == null) return "dias-neutro";
  if (d < 0) return "dias-atraso";
  if (d <= 1) return "dias-urgente";
  if (d <= 3) return "dias-pronto";
  return "dias-ok";
});

const clasePrioridadHeader = computed(() => ({
  "header-alta": props.pedido.prioridad === "Alta",
  "header-media": props.pedido.prioridad === "Media",
  "header-baja": props.pedido.prioridad === "Baja",
}));

const claseUrgencia = computed(() => {
  const d = props.pedido.dias_restantes;
  if (d == null) return "";
  if (d < 0) return "card-atraso";
  if (d <= 1) return "card-urgente";
  return "";
});

const claseEstatus = computed(() => ({
  "estatus-proceso": props.pedido.estatus_almacen === "En proceso",
  "estatus-listo": props.pedido.estatus_almacen === "Listo para entrega",
}));
</script>

<style scoped>
/* ── Card base ───────────────────────────────────────────── */
.pds-card {
  background: var(--d-cream);
  border-radius: 12px;
  border: 2px solid rgba(64, 34, 8, 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  /* Sombra sutil para que la card destaque sobre el fondo */
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.pds-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
}
.pds-card.card-atraso {
  border-color: #c0392b;
  background: #fff8f8;
}
.pds-card.card-urgente {
  border-color: #b7770d;
  background: #fffdf5;
}

/* ── Header de la card ──────────────────────────────────── */
.pds-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.45rem 0.85rem;
  flex-shrink: 0;
  gap: 0.5rem;
}
.header-alta {
  background: #c0392b;
}
.header-media {
  background: #b98d46;
}
.header-baja {
  background: #2e7d32;
}

.pds-header-left {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 0;
}
.pds-numero {
  font-family: var(--d-font-mono);
  font-size: 1.15rem;
  font-weight: 900;
  color: white;
  letter-spacing: 0.02em;
  white-space: nowrap;
}
.pds-prioridad-tag {
  font-size: 0.65rem;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.85);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  background: rgba(0, 0, 0, 0.2);
  padding: 2px 7px;
  border-radius: 20px;
  white-space: nowrap;
}
.pds-estatus {
  font-size: 0.7rem;
  font-weight: 800;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;
}
.estatus-proceso {
  background: rgba(255, 255, 255, 0.92);
  color: #1565c0;
}
.estatus-listo {
  background: rgba(255, 255, 255, 0.92);
  color: #1b5e20;
}

/* ── Cliente ────────────────────────────────────────────── */
.pds-cliente-bloque {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0.5rem 0.85rem 0.4rem;
  border-bottom: 1px solid rgba(64, 34, 8, 0.1);
  flex-shrink: 0;
}
.pds-cliente-num {
  font-family: var(--d-font-mono);
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--d-green);
}
.pds-cliente-nombre {
  font-size: 0.95rem;
  font-weight: 900;
  color: var(--d-brown);
  line-height: 1.25;
  /* Permitir wrap natural, sin cortar con overflow hidden */
  word-break: break-word;
}

/* ── Métricas ────────────────────────────────────────────── */
.pds-metricas {
  display: flex;
  padding: 0.5rem 0.85rem;
  border-bottom: 1px solid rgba(64, 34, 8, 0.08);
  flex-shrink: 0;
  gap: 0.25rem;
}
.pds-metrica {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.pds-metrica-center {
  align-items: center;
  text-align: center;
}
.pds-metrica-right {
  align-items: flex-end;
  text-align: right;
}
.pds-m-label {
  font-size: 0.58rem;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--d-green);
  letter-spacing: 0.04em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pds-m-val {
  font-size: 0.88rem;
  font-weight: 800;
  color: var(--d-brown);
  white-space: nowrap;
}
.pds-dias {
  font-size: 0.95rem;
  font-weight: 900;
  white-space: nowrap;
}
.dias-ok {
  color: #2e7d32;
}
.dias-pronto {
  color: #b7770d;
}
.dias-urgente {
  color: #e65100;
}
.dias-atraso {
  color: #c0392b;
}
.dias-neutro {
  color: rgba(64, 34, 8, 0.35);
}

/* ── Notas ──────────────────────────────────────────────── */
.pds-notas {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  padding: 0.4rem 0.85rem;
  background: rgba(96, 108, 56, 0.07);
  flex: 1;
}
.pds-notas-icon {
  font-size: 0.75rem;
  color: var(--d-green);
  flex-shrink: 0;
  margin-top: 2px;
}
.pds-notas-texto {
  font-size: 0.78rem;
  color: var(--d-brown);
  font-weight: 600;
  line-height: 1.35;
  word-break: break-word;
}

/* ── Capturista ─────────────────────────────────────────── */
.pds-capturista {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.85rem;
  font-size: 0.66rem;
  color: rgba(64, 34, 8, 0.45);
  font-weight: 600;
  border-top: 1px solid rgba(64, 34, 8, 0.06);
}
.pds-capturista i {
  font-size: 0.62rem;
}

/* ── Ajustes para móvil (1 columna) ─────────────────────── */
@media (max-width: 599px) {
  /* En móvil la card puede ser más ancha, damos un poco más de aire */
  .pds-card-header {
    padding: 0.5rem 1rem;
  }
  .pds-numero {
    font-size: 1.25rem;
  }
  .pds-cliente-bloque {
    padding: 0.55rem 1rem 0.45rem;
  }
  .pds-cliente-nombre {
    font-size: 1rem;
  }
  .pds-metricas {
    padding: 0.55rem 1rem;
  }
  .pds-m-label {
    font-size: 0.62rem;
  }
  .pds-m-val {
    font-size: 0.95rem;
  }
  .pds-dias {
    font-size: 1rem;
  }
  .pds-notas {
    padding: 0.45rem 1rem;
  }
  .pds-notas-texto {
    font-size: 0.82rem;
  }
}

/* ── Ajustes para desktop/TV ─────────────────────────────── */
@media (min-width: 1024px) {
  .pds-numero {
    font-size: 1.25rem;
  }
  .pds-cliente-nombre {
    font-size: 1rem;
  }
  .pds-m-label {
    font-size: 0.62rem;
  }
  .pds-m-val {
    font-size: 0.95rem;
  }
  .pds-dias {
    font-size: 1rem;
  }
}
</style>
